import { incomeTaxYear } from "@stb/jurisdiction-au/income-tax"

import { displayYear, formatCheckedOn } from "../components/home/home-content"
import { formatAud, SimpleTable, SourceLine } from "../components/landing"
import { PayCalculator } from "../components/landing/pay-calculator"
import {
  DEFAULT_FY,
  studyLoanRange,
  studyLoanRule,
  variantAnswer,
} from "../components/landing/pay-calculator-model"
import type { Guide } from "./guide-types"

/** Served at /hecs-repayment-calculator (T917). Copy source: docs/design/html/flows/web-landing-tool-hecs.mjs and docs/specs/pay-calculator-spec.md section 9. */
const year = incomeTaxYear(DEFAULT_FY)
const fy = displayYear(DEFAULT_FY)
const source = year.studyLoan.source
const checked = formatCheckedOn(source.checkedOn)

const dollars = (n: number) => `A$${n.toLocaleString("en-AU")}`

const bands = year.studyLoan.bands
const bandRows = bands.map((b, k) => ({
  header: studyLoanRange(bands, k),
  cells: [studyLoanRule(b)],
}))
const minimum = dollars(bands.find((b) => b.rateBp > 0)?.from ?? 0)

export const hecsGuide: Guide = {
  slug: "hecs-repayment-calculator",
  title: `HECS-HELP Repayment Calculator ${fy}`,
  description: `Work out your HECS-HELP repayment for ${fy} from your income. Per year, fortnight or week, with ATO thresholds and take-home pay. Free.`,
  crumb: "HECS-HELP repayment calculator",
  h1: `HECS-HELP repayment calculator (${fy})`,
  published: "2026-10-30",
  reviewed: "2026-10-30",
  badge: "Free tool",
  financialYear: fy,
  checked,
  sources: [{ label: "ATO, study and training loan repayment income and rates", href: source.url }],
  answer: variantAnswer("help", formatAud),
  answerMore: (
    <>
      The calculator applies the ATO repayment rates for {fy} to your taxable income and shows the
      repayment next to your income tax, Medicare levy and take-home pay. Enter your own pay below.
    </>
  ),
  related: [
    { label: "Pay calculator: your take-home pay after tax", href: "/pay-calculator" },
    {
      label: "Medicare levy calculator: the levy and the low-income reduction",
      href: "/medicare-levy-calculator",
    },
    { label: "Cents per km calculator: your car expense claim", href: "/cents-per-km-calculator" },
    { label: "Invoicing for sole traders: send invoices, see who has paid", href: "/invoicing" },
  ],
  toc: [
    { id: "calculator", label: "Work out your repayment" },
    { id: "thresholds", label: `Repayment thresholds ${fy}` },
    { id: "how", label: "How repayments work" },
    { id: "faq", label: "Common questions" },
  ],
  cta: {
    title: "Self-employed with a HELP debt? Keep the books in one place",
    description:
      "Your repayment is worked out when you lodge, so ABN income needs a plan. Kestly keeps your invoices, receipts and trips ready for tax time.",
    ctaLabel: "Start free",
    ctaHref: "/login?from=hecs-repayment-calculator",
  },
  body: (
    <>
      <section aria-labelledby="calculator" className="grid gap-4">
        <h2 id="calculator" className="text-2xl font-semibold">
          Work out your repayment
        </h2>
        <PayCalculator variant="help" />
      </section>

      <section aria-labelledby="thresholds" className="grid gap-3">
        <h2 id="thresholds" className="text-2xl font-semibold">
          HELP repayment thresholds {fy}
        </h2>
        <p className="max-w-[70ch]">
          No repayment is due until your repayment income passes {minimum}. After that the rate
          depends on which band your income falls in. HELP (HECS-HELP, FEE-HELP, SA-HELP, OS-HELP),
          VET Student Loans and other study and training loans share one repayment.
        </p>
        <SimpleTable
          caption={`HELP repayment thresholds ${fy}`}
          columns={["Repayment income", `Compulsory repayment (${fy})`]}
          rows={bandRows}
        />
        <SourceLine
          sources={[{ label: "ATO", href: source.url }]}
          financialYear={fy}
          checked={checked}
        />
      </section>

      <section aria-labelledby="how" className="grid max-w-[70ch] gap-3">
        <h2 id="how" className="text-2xl font-semibold">
          How HELP repayments work
        </h2>
        <p>
          The repayment is not taken out of your pay as a separate bill. Your employer withholds
          extra tax each pay if you tell them you have a study loan, and the ATO works out the
          actual amount when you lodge your return. Whatever was withheld counts toward it.
        </p>
        <p>
          The ATO uses your repayment income, which is your taxable income plus some other items
          such as reportable fringe benefits and net investment losses. This calculator uses your
          taxable income (the standard deduction is left out unless you tick it), so it can differ
          if you have those items.
        </p>
        <p>
          Voluntary repayments are not included. Your debt is also indexed each 1 June, which
          changes what you owe but not the compulsory repayment shown here.
        </p>
      </section>
    </>
  ),
  faq: [
    {
      question: "What is the HELP repayment threshold this year?",
      answer: `For ${fy} the minimum repayment income is ${minimum}. Below that you make no compulsory repayment. The full table is above, from the ATO.`,
    },
    {
      question: "Is HECS the same as HELP?",
      answer:
        "HECS-HELP is one kind of HELP loan, alongside FEE-HELP, SA-HELP and OS-HELP. VET Student Loans and other study and training loans are separate, but they all share one compulsory repayment worked out from your income.",
    },
    {
      question: "How is my repayment worked out?",
      answer:
        "The ATO looks at your repayment income for the year and applies the rate for the band it falls in. The bands are in the table above and the calculator applies them to your pay.",
    },
    {
      question: "Does my repayment reduce my take-home pay?",
      answer:
        "Yes, if you tell your employer you have a study loan. They withhold extra tax each pay toward it. The calculator shows the repayment as its own line before your take-home pay.",
    },
    {
      question: "What counts as repayment income?",
      answer:
        "Taxable income plus reportable fringe benefits, reportable super contributions, net investment losses and some exempt foreign income. This calculator uses taxable income only.",
    },
    {
      question: "Can I pay off my HELP debt faster?",
      answer:
        "Yes. You can make voluntary repayments to the ATO at any time. This calculator shows the compulsory repayment only.",
    },
    {
      question: "Do I repay if I live abroad?",
      answer:
        "Yes. If you live abroad for more than six months you must report your income from all sources to the ATO and make repayments on the same basis. This calculator does not cover that.",
    },
    {
      question: "What if I earn on an ABN?",
      answer:
        "The repayment is still worked out from your income when you lodge, but no tax is withheld as you go, so you set the money aside yourself. Use the pay calculator for your employee income and keep your ABN records separate.",
    },
    {
      question: "Does the ATO change the thresholds every year?",
      answer:
        "Yes. The thresholds are updated for each income year. We update the calculator when the ATO publishes them and show the date we last checked beside the table.",
    },
  ],
}
